/* eslint-disable func-names */
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import * as Yup from 'yup';

const gtZero = (value) => value !== null && value !== undefined && value > 0;

export const useFornecedorSchema = () => {
  return Yup.object().shape({
    id: Yup.string()
      .nullable()
      .required(mensagemCampoObrigatorio('o fornecedor')),
  });
};

export const useCategoriaSchema = () => {
  return Yup.string()
    .nullable()
    .required(mensagemCampoObrigatorio('a categoria'));
};

export const useCategoriasSchema = () => {
  return Yup.array()
    .min(1)
    .of(
      Yup.object().shape({
        classe: Yup.object()
          .nullable()
          .required(mensagemCampoObrigatorio('a categoria'))
          .test('classe', mensagemCampoObrigatorio('a categoria'), function(classe) {
            return !!classe && !!classe?.id;
          }),
        valorBruto: Yup.number()
          .nullable()
          .required(mensagemCampoObrigatorio('o valor bruto')),
        descricao: Yup.string().nullable(),
      })
    );
};

export const useDiferencaCategoriasSchema = () => {
  return Yup.number().test('diferencaCategorias', 'A diferença deve ser R$0,00', function(diferencaCategorias) {
    return !diferencaCategorias;
  });
};

export const useProdutosSchema = () => {
  return Yup.array(
    Yup.object().shape({
      produto: Yup.object()
        .nullable()
        .required(mensagemCampoObrigatorio('o produto')),
      quantidade: Yup.number()
        .nullable()
        .required(mensagemCampoObrigatorio('a quantidade'))
        .test('quantidadeGtZero', 'A quantidade deve ser superior a 0', gtZero),
      valor: Yup.number()
        .nullable()
        .required(mensagemCampoObrigatorio('o valor'))
        .test('quantidadeGtZero', 'O valor deve ser superior a 0', gtZero),
    })
  );
};

export const usePagamentoSchema = () => {
  return Yup.object()
    .nullable()
    .shape({
      avista: Yup.object()
        .nullable()
        .shape({
          dataVencimento: Yup.object()
            .nullable()
            .test('dataVencimento', mensagemCampoObrigatorio('a data de vencimento'), function(dataVencimento) {
              return this.parent?.condicaoPagamento === 'A_VISTA' ? !!dataVencimento : true;
            }),
          dataPagamento: Yup.date()
            .nullable()
            .test('dataPagamento', mensagemCampoObrigatorio('a data de pagamento'), function(dataPagamento) {
              return this.parent?.condicaoPagamento === 'A_VISTA' && this.parent.compraPaga ? !!dataPagamento : true;
            })
            .max(Yup.ref('dataLiquidacao'), 'A data de pagamento não pode ser superior à data de liquidação.'),
          dataLiquidacao: Yup.date()
            .nullable()
            .test('dataLiquidacao', mensagemCampoObrigatorio('a data de liquidação'), function(dataLiquidacao) {
              return this.parent?.condicaoPagamento === 'A_VISTA' && this.parent.compraPaga ? !!dataLiquidacao : true;
            })
            .min(Yup.ref('dataPagamento'), 'A data de liquidação não pode ser inferior à data de pagamento.'),
          conta: Yup.object()
            .nullable()
            .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
              return this.parent?.condicaoPagamento === 'A_VISTA' && this.parent.compraPaga ? !!conta : true;
            }),
          formaPagamento: Yup.string()
            .nullable()
            .test('dataPagamento', mensagemCampoObrigatorio('a forma de pagamento'), function(formaPagamento) {
              return this.parent?.condicaoPagamento === 'A_VISTA' && this.parent.compraPaga ? !!formaPagamento : true;
            }),
          numeroCheque: Yup.string()
            .nullable()
            .test('numeroCheque', mensagemCampoObrigatorio('o número do cheque'), function(numeroCheque) {
              return this.parent?.condicaoPagamento === 'A_VISTA' &&
                this.parent.compraPaga &&
                this.parent?.formaPagamento === 'CHEQUE'
                ? !!numeroCheque
                : true;
            }),
        }),
      parcelado: Yup.object()
        .nullable()
        .shape({
          quantidade: Yup.number()
            .nullable()
            .test('quantidade', mensagemCampoObrigatorio('a quantidade'), function(quantidade) {
              return this.parent.condicaoPagamento !== 'PARCELADO' || (quantidade !== undefined && quantidade !== null);
            })
            .test('minimo', 'A quantidade deve ser superior a 0', function(quantidade) {
              return this.parent.condicaoPagamento !== 'PARCELADO' || quantidade > 0;
            })
            .test('maximo', 'A quantidade de parcelas deve ser de no máximo 72', function(quantidade) {
              return this.parent.condicaoPagamento !== 'PARCELADO' || quantidade <= 72;
            }),
          diferencaParcelas: Yup.number().test('diferencaParcelas', 'A diferença deve ser R$0,00', function(
            diferencaParcelas
          ) {
            return this.parent.condicaoPagamento !== 'PARCELADO' || !diferencaParcelas;
          }),
          parcelas: Yup.array(
            Yup.object()
              .nullable()
              .shape({
                dataVencimento: Yup.object()
                  .nullable()
                  .required(mensagemCampoObrigatorio('a data de vencimento')),
                valorParcela: Yup.number()
                  .nullable()
                  .test('valorParcela', 'O valor deve ser maior que 0', function(valor) {
                    return (
                      this.parent.condicaoPagamento !== 'PARCELADO' ||
                      (valor !== undefined && valor !== null && valor >= 0)
                    );
                  })
                  .required(mensagemCampoObrigatorio('o valor da parcela')),
              })
          ),
        }),
    });
};

export const useAproveitamentoCreditoSchema = () => {
  return Yup.object().shape({
    tipoAproveitamentoCredito: Yup.string()
      .nullable()
      .required('Informe o tipo de aproveitamento de crédito'),

    percentualCredito: Yup.number()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: 'CREDITO_PARCIAL',
        then: Yup.number()
          .nullable()
          .required('Informe o percentual de crédito')
          .min(0, 'O percentual de crédito deve ser maior ou igual a 0')
          .max(100, 'O percentual de crédito deve ser menor ou igual a 100'),
      }),
    aliquotaIbs: Yup.number()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: (value) => ['CREDITO_PARCIAL', 'CREDITO_INTEGRAL'].includes(value),
        then: Yup.number()
          .nullable()
          .required('Informe a alíquota IBS')
          .min(0, 'O percentual da alíquota deve ser maior ou igual a 0')
          .max(100, 'O percentual da alíquota deve ser menor ou igual a 100'),
      }),
    aliquotaCbs: Yup.number()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: (value) => ['CREDITO_PARCIAL', 'CREDITO_INTEGRAL'].includes(value),
        then: Yup.number()
          .nullable()
          .required('Informe a alíquota CBS')
          .min(0, 'O percentual da alíquota deve ser maior ou igual a 0')
          .max(100, 'O percentual da alíquota deve ser menor ou igual a 100'),
      }),
    observacaoJustificativa: Yup.string()
      .nullable()
      .when('tipoAproveitamentoCredito', {
        is: 'CREDITO_PARCIAL',
        then: Yup.string()
          .nullable()
          .required('Informe a justificativa para aproveitamento de crédito parcial'),
      }),
  });
};
